import React, { useEffect, useState } from "react";
import { useAppSelector } from "app/store";
import { useGaiaObjectUiContent } from "features/ui-content/hooks";
import {
  Box,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  ColumnLayout,
  Container,
  Header,
  Icon,
  Input,
  Select,
  SpaceBetween,
  Spinner,
  SplitPanel,
} from "@amzn/awsui-components-react";
import { FilePreview } from "components/file-preview";
import { isFileTypePreviewable } from "components/file-preview/utils";
import { FilePreviewSplitPanelProps } from "./definitions";
import { Document } from "models/schemas";
import {
  useGetDocumentByIdQuery,
  useDownloadUrlQuery,
  useLazyDownloadUrlQuery,
  useDeleteDocumentMutation,
  useUpdateDocumentMutation,
  useUpdateDocumentForMoveMutation,
} from "components/documents-table/endpoints";
import { useNotifications } from "features/notifications";
import { datePopOver } from "utils/date-format";
import { ValueWithLabel } from "utils/value-with-label";
import CommonDeleteModal from "components/common-delete-modal";
import MoveFoldersModal from "components/move-folders-modal";
import { useFileOperations } from "components/documents-table/file-operations";
import { useListDocumentsQuery } from "components/documents-table/endpoints";
import {
  useRegisterDocumentMutation,
  useUploadUrlMutation,
} from "components/create-audit-form/endpoints";
import { getFileNameWithExtension } from "components/documents-table/utils";
import styles from "components/documents-table/style.module.scss";

const FilePreviewSplitPanel: React.FC<FilePreviewSplitPanelProps> = ({
  documentId,
  isFilesTab,
  gaiaObjectType,
}) => {
  const { addNotification } = useNotifications();
  const uiContent: any = useGaiaObjectUiContent(gaiaObjectType);
  const [deleteDocCall] = useDeleteDocumentMutation();
  const [updateDocCall] = useUpdateDocumentMutation();
  const [updateDocumentForMoveCall] = useUpdateDocumentForMoveMutation();
  const [lazyDownloadUrlCall] = useLazyDownloadUrlQuery();

  const [document, setDocument] = useState<Document | null>(null);
  const [url, setUrl] = useState<string>("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [docToRename, setDocToRename] = useState<Document | undefined>();
  const [fileName, setFileName] = useState("");
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [isEditingType, setIsEditingType] = useState(false);
  const [isTeamFileLevel, setIsTeamFileLevel] = useState(false);
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const teamName = useAppSelector((state) => state.audits.teamName);
  const currentUser = useAppSelector((state) => state.auth.user.alias);
  const [registerDocCall] = useRegisterDocumentMutation();
  const [uploadUrlCall] = useUploadUrlMutation();

  const { data: documentsData } = useListDocumentsQuery(
    { parentId: teamName },
    { skip: !isTeamFileLevel || !teamName }
  );

  // Fetch document data
  const { data: docData, isLoading: isDocumentLoading } =
    useGetDocumentByIdQuery(
      { documentId: documentId! },
      {
        refetchOnMountOrArgChange: true,
        skip: !documentId,
      }
    );

  // Fetch download URL for preview
  const { data: downloadUrl } = useDownloadUrlQuery(
    { documentId: documentId!, preview: true },
    {
      skip: !documentId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
      pollingInterval: 0,
    }
  );

  useEffect(() => {
    if (docData?.document) {
      // Handle both array and single document responses
      const doc = Array.isArray(docData.document)
        ? docData.document[0]
        : docData.document;
      setDocument(doc);
      setSelectedDocumentType(doc.documentType);
      setIsTeamFileLevel(doc.documentType === "Team");
    }
  }, [docData]);

  useEffect(() => {
    if (downloadUrl) {
      setUrl(downloadUrl.url);
    }
  }, [downloadUrl]);

  // Initialize file operations hook
  const { moveFiles } = useFileOperations({
    uploadUrlCall,
    registerDocCall,
    deleteDocCall,
    updateDocCall,
    updateDocumentForMoveCall,
    lazyDownloadUrlCall,
    ingestDocuments: undefined,
    deleteIngestedDocuments: undefined,
    addNotification,
    teamName,
    currentUser,
    parentId: document?.parentId || "",
    gaiaObjectId: "",
    category: "team-files",
    displayMode: "flat",
    setFlatDocs: () => {},
    setDocs: () => {},
    setSelectedItems: () => {},
    setSingleDoc: () => {},
    setDeleteDocModalIsVisible: () => {},
    setDocToRename: () => {},
    setFileName: () => {},
  });

  // Handle path move operation
  const handlePathMove = async (files: Document[], targetParentId: string) => {
    await moveFiles(files, targetParentId);
    // Update local document state after successful move
    setDocument((prev) =>
      prev ? { ...prev, parentId: targetParentId } : null
    );
    setMoveModalVisible(false);
  };

  const handleDelete = async () => {
    if (!document) return;

    try {
      await deleteDocCall({ documentId: document.documentId }).unwrap();
      addNotification({
        type: "success",
        header: "File deleted.",
        dismissible: true,
        timeout: 5000,
      });
    } catch (e) {
      addNotification({
        type: "error",
        header: "File delete failed.",
        dismissible: true,
        timeout: 5000,
      });
    }
  };

  const handleRename = async () => {
    if (!docToRename || !fileName.trim()) return;

    try {
      await updateDocCall({
        documentId: docToRename.documentId,
        fileName: fileName.trim(),
      }).unwrap();

      setDocument((prev) =>
        prev ? { ...prev, fileName: fileName.trim() } : null
      );
      setDocToRename(undefined);
      setFileName("");

      addNotification({
        type: "success",
        header: "File renamed successfully.",
        dismissible: true,
        timeout: 5000,
      });
    } catch (e) {
      addNotification({
        type: "error",
        header: "File rename failed.",
        dismissible: true,
        timeout: 5000,
      });
    }
  };

  const handleDocumentTypeChange = async (newType: string) => {
    if (!document) return;

    try {
      await updateDocCall({
        documentId: document.documentId,
        fileName: document.fileName,
        documentType: newType,
      }).unwrap();

      setDocument((prev) => (prev ? { ...prev, documentType: newType } : null));
      setSelectedDocumentType(newType);
      setIsEditingType(false);

      addNotification({
        type: "success",
        header: "Document type updated successfully.",
        dismissible: true,
        timeout: 5000,
      });
    } catch (e) {
      addNotification({
        type: "error",
        header: "Document type update failed.",
        dismissible: true,
        timeout: 5000,
      });
    }
  };

  const handleDownload = async () => {
    if (!document?.s3Prefix) return;

    try {
      const response = await lazyDownloadUrlCall({
        documentId: document.documentId,
        preview: false,
      }).unwrap();

      if (response?.url) {
        window.location.href = response.url;
      }
    } catch (e) {
      addNotification({
        type: "error",
        header: "File download failed.",
        dismissible: true,
        timeout: 5000,
      });
    }
  };

  const handleOpenInNewTab = () => {
    if (!url) {
      addNotification({
        type: "error",
        header: "Preview URL not available.",
        dismissible: true,
        timeout: 5000,
      });
      return;
    }
    window.open(url, "_blank");
  };

  const handleActionClick = (
    event: CustomEvent<ButtonDropdownProps.ItemClickDetails>
  ) => {
    const actionId = event.detail.id;
    const firstDotIndex = actionId.indexOf(".");
    const action = actionId.substring(0, firstDotIndex);
    const docId = actionId.substring(firstDotIndex + 1);

    switch (action) {
      case "open":
        handleOpenInNewTab();
        break;
      case "ed":
        if (document) {
          setDocToRename(document);
          setFileName(document.fileName);
        }
        break;
      case "rm":
        setDeleteModalVisible(true);
        break;
    }
  };

  const getHeaderTitle = () => {
    if (!document) return "File Preview";
    if (document.documentId === docToRename?.documentId) {
      return "Renaming file...";
    }
    return getFileNameWithExtension(document);
  };

  const renderFileNameEditor = () => {
    if (!document || document.documentId !== docToRename?.documentId)
      return null;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "16px",
          width: "100%",
        }}
      >
        <div style={{ flex: 1, width: "100%" }}>
          <Input
            onChange={({ detail }) => setFileName(detail.value)}
            value={fileName}
          />
        </div>
        <span>{document.extentionType}</span>
        <span
          onClick={() => {
            setDocToRename(undefined);
          }}
          className={styles.renameButton}
        >
          <Icon name="close"></Icon>
        </span>
        <span onClick={handleRename} className={styles.renameButton}>
          <Icon name="check"></Icon>
        </span>
      </div>
    );
  };

  const renderDocumentType = () => {
    if (!document) return null;

    if (isEditingType) {
      const selectOptions =
        uiContent.documentTypeOptions?.map((option: any) => ({
          label: option.label,
          value: option.value,
        })) || [];

      const selectedOption =
        selectOptions.find((opt: any) => opt.value === selectedDocumentType) ||
        null;

      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Select
            selectedOption={selectedOption}
            onChange={({ detail }) =>
              handleDocumentTypeChange(detail.selectedOption?.value || "")
            }
            options={selectOptions}
            placeholder="Select document type"
          />
          <Button
            variant="icon"
            iconName="close"
            onClick={() => setIsEditingType(false)}
          />
        </div>
      );
    }

    const currentOption = uiContent.documentTypeOptions?.find(
      (opt: any) => opt.value === selectedDocumentType
    );

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span>{currentOption?.label || selectedDocumentType}</span>
        <Button
          variant="icon"
          iconName="edit"
          onClick={() => setIsEditingType(true)}
        />
      </div>
    );
  };

  const renderPath = () => {
    if (!document || !isTeamFileLevel) return null;

    const currentPath = `/${document.parentId
      .split("/")
      .filter(Boolean)
      .slice(1)
      .join("/")}`;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span>{currentPath || "/"}</span>
        <Button
          variant="icon"
          iconName="edit"
          onClick={() => setMoveModalVisible(true)}
          ariaLabel="Edit file location"
        />
      </div>
    );
  };

  if (!documentId) {
    return (
      <SplitPanel
        header="File Preview"
        i18nStrings={{
          closeButtonAriaLabel: "Close panel",
          openButtonAriaLabel: "Open panel",
        }}
        closeBehavior={isFilesTab ? "collapse" : "hide"}
      >
        <Box textAlign="center" padding="l">
          Select a file to preview
        </Box>
      </SplitPanel>
    );
  }
  return (
    <>
      <SplitPanel
        header={getHeaderTitle()}
        i18nStrings={{
          closeButtonAriaLabel: "Close panel",
          openButtonAriaLabel: "Open panel",
        }}
        closeBehavior={isFilesTab ? "collapse" : "hide"}
        hidePreferencesButton
      >
        {renderFileNameEditor()}
        {isDocumentLoading ? (
          <Container>
            <Box textAlign="center">
              <Spinner /> Loading document...
            </Box>
          </Container>
        ) : document ? (
          <SpaceBetween direction="vertical" size="l">
            <Container
              header={
                <Header
                  variant="h2"
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <ButtonDropdown
                        items={[
                          {
                            id: `open.${document.documentId}`,
                            text: "Open in new tab",
                            disabled: !isFileTypePreviewable(
                              document.extentionType
                            ),
                            disabledReason:
                              "Preview not available for this file type.",
                          },
                          {
                            id: `ed.${document.documentId}`,
                            text: "Rename",
                          },
                          {
                            id: `rm.${document.documentId}`,
                            text: "Delete",
                          },
                        ]}
                        onItemClick={handleActionClick}
                      >
                        Actions
                      </ButtonDropdown>
                      <Button onClick={handleDownload} variant="primary">
                        Download
                      </Button>
                    </SpaceBetween>
                  }
                >
                  Details
                </Header>
              }
            >
              <ColumnLayout columns={2} variant="text-grid">
                <SpaceBetween direction="vertical" size="l">
                  {!isTeamFileLevel ? (
                    <ValueWithLabel label="Type">
                      {renderDocumentType()}
                    </ValueWithLabel>
                  ) : (
                    <ValueWithLabel label="Path">{renderPath()}</ValueWithLabel>
                  )}
                  <ValueWithLabel label="File size">
                    {document.size}
                  </ValueWithLabel>
                </SpaceBetween>
                <SpaceBetween direction="vertical" size="l">
                  <ValueWithLabel label="Last updated">
                    {datePopOver(Number(document.updatedAt))}
                  </ValueWithLabel>
                  <ValueWithLabel label="Updated by">
                    {document.updatedBy}
                  </ValueWithLabel>
                </SpaceBetween>
              </ColumnLayout>
            </Container>

            <Container header={<Header variant="h2">Preview</Header>}>
              {url && document.fileName && (
                <FilePreview
                  type={document.extentionType}
                  fileName={document.fileName}
                  presignedUrl={url}
                />
              )}
            </Container>
          </SpaceBetween>
        ) : (
          <Container>
            <Box textAlign="center">Document not found</Box>
          </Container>
        )}
      </SplitPanel>

      <CommonDeleteModal
        setVisible={setDeleteModalVisible}
        visible={deleteModalVisible}
        onDelete={handleDelete}
        items={document}
        title="Delete file?"
        message={<>Permanently delete {document?.fileName}?</>}
      />
      {isTeamFileLevel && document && (
        <MoveFoldersModal
          visible={moveModalVisible}
          setVisible={setMoveModalVisible}
          selectedFiles={[document]}
          allDocuments={documentsData?.documents || []}
          teamName={teamName}
          onMove={handlePathMove}
        />
      )}
    </>
  );
};

export default FilePreviewSplitPanel;
